import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Phone, Utensils, Clock } from 'lucide-react';
import { restaurantInfo } from '../data/mock';
import TemporaryNotice from '../components/TemporaryNotice';

const Home = () => {
  const features = [
    { icon: Star, text: '5.0 Google értékelés', color: 'text-[#FFA726]' },
    { icon: Utensils, text: 'Híres magyar lángos', color: 'text-[#00BCD4]' },
    { icon: Utensils, text: 'Retro hamburgerek', color: 'text-[#FFA726]' },
    { icon: Clock, text: 'Gyors, barátságos kiszolgálás', color: 'text-[#00BCD4]' }
  ];

  return (
    <div className="min-h-screen vibrant-gradient-bg">
      {/* Temporary Notice */}
      <TemporaryNotice />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-tight">
                  <span className="text-gray-900">Retro Magyar</span>
                  <br />
                  <span className="bg-gradient-to-r from-[#00BCD4] to-[#FFA726] bg-clip-text text-transparent">
                    Street Food
                  </span>
                  <br />
                  <span className="text-gray-900">Szigethalmon</span>
                </h1>
                <p className="text-xl sm:text-2xl text-gray-600 font-medium">
                  Friss Lángos, Retro Burgerek és Barátságos Kiszolgálás
                </p>
                <p className="text-lg text-gray-800 italic font-semibold drop-shadow-sm">
                  "{restaurantInfo.tagline}"
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/menu"
                  className="px-8 py-4 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  Étlap megtekintése
                </Link>
                <Link
                  to="/cart"
                  className="px-8 py-4 bg-gradient-to-r from-[#FFA726] to-[#FF9800] text-white rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  Rendelés
                </Link>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="tel:+36309517159"
                  className="inline-flex items-center justify-center space-x-2 px-6 py-3 border-2 border-gray-900 text-gray-900 rounded-xl font-semibold hover:bg-gray-900 hover:text-white transition-all duration-300"
                >
                  <Phone className="w-5 h-5" />
                  <span>Hívás most</span>
                </a>
                <a
                  href="https://www.google.com/maps/dir//2315+Szigethalom,+Sz%C3%A9chenyi+u.+44"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center space-x-2 px-6 py-3 border-2 border-gray-900 text-gray-900 rounded-xl font-semibold hover:bg-gray-900 hover:text-white transition-all duration-300"
                >
                  <MapPin className="w-5 h-5" />
                  <span>Útvonal</span>
                </a>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#00BCD4] to-[#FFA726] rounded-3xl blur-2xl opacity-20"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_hungarian-bufet/artifacts/xo54yio7_IMG_20250702_084700.jpg"
                  alt="Boldog Büfé - Friss Hamburger"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <div className="p-4 bg-white rounded-full shadow-md mb-4">
                  <feature.icon className={`w-8 h-8 ${feature.color}`} />
                </div>
                <p className="text-gray-900 font-bold text-lg">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Items Preview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              Népszerű <span className="text-[#FFA726]">Ételeink</span>
            </h2>
            <p className="text-xl text-gray-600">
              Ismerd meg a vendégeink kedvenceit!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Lángos */}
            <div className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_hungarian-bufet/artifacts/1ousryvp_IMG_20240927_080657.jpg"
                  alt="Lángos"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <h3 className="text-2xl font-black text-white mb-1">Lángos</h3>
                  <p className="text-white/90 text-sm">1090 Ft-tól</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Friss, ropogós lángos, sokféle feltéttel. A magyar street food klasszikusa!
                </p>
                <Link
                  to="/menu"
                  className="inline-block w-full text-center px-4 py-3 bg-[#00BCD4] text-white rounded-xl font-semibold hover:bg-[#00ACC1] transition-colors"
                >
                  Rendelés
                </Link>
              </div>
            </div>

            {/* Hamburger */}
            <div className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_hungarian-bufet/artifacts/3sf8zp5z_IMG_20250528_114326.jpg"
                  alt="Hamburger"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <h3 className="text-2xl font-black text-white mb-1">Retro Hamburger</h3>
                  <p className="text-white/90 text-sm">2350 Ft-tól</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Klasszikus ízek, friss hozzávalók. A 25 év óta legjobb hamburger!
                </p>
                <Link
                  to="/menu"
                  className="inline-block w-full text-center px-4 py-3 bg-[#00BCD4] text-white rounded-xl font-semibold hover:bg-[#00ACC1] transition-colors"
                >
                  Rendelés
                </Link>
              </div>
            </div>

            {/* Hot Dog */}
            <div className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_hungarian-bufet/artifacts/74hhw44w_IMG_20240822_172927.jpg"
                  alt="Hot Dog"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <h3 className="text-2xl font-black text-white mb-1">Hot-dog</h3>
                  <p className="text-white/90 text-sm">1150 Ft-tól</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Klasszikus, sajtos-baconos vagy chilis változatban. Gyors, finom!
                </p>
                <Link
                  to="/menu"
                  className="inline-block w-full text-center px-4 py-3 bg-[#00BCD4] text-white rounded-xl font-semibold hover:bg-[#00ACC1] transition-colors"
                >
                  Rendelés
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#00BCD4] to-[#FFA726] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
            Éhes vagy? Rendelj most!
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Helyben fogyasztás, elvitel vagy házhozszállítás – Ahogy neked a legkényelmesebb!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/menu"
              className="px-8 py-4 bg-white text-[#00BCD4] rounded-xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
            >
              Étlap és Rendelés
            </Link>
            <a
              href="tel:+36309517159"
              className="px-8 py-4 bg-gray-900 text-white rounded-xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
            >
              Hívás: +36 30 951 7159
            </a>
          </div>
        </div>
      </section>

      {/* Location Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6">
                Látogass el <span className="text-[#FFA726]">hozzánk!</span>
              </h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-6 h-6 text-[#00BCD4] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">{restaurantInfo.address.zip} {restaurantInfo.address.city}</p>
                    <p>{restaurantInfo.address.street}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-6 h-6 text-[#00BCD4] flex-shrink-0" />
                  <a href={`tel:${restaurantInfo.phone}`} className="font-semibold text-gray-900 hover:text-[#00BCD4] transition-colors">
                    {restaurantInfo.phone}
                  </a>
                </div>
              </div>
              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  Nyitvatartás és Útvonal
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_5db5e26f-1718-47ba-8329-c17dba22ad5b/artifacts/2q6h4h5w_K%C3%A9perny%C5%91k%C3%A9p%202026-03-12%20111822.png"
                alt="Boldog Büfé"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
